import type { ElementType, ReactNode } from 'react';
import type { To } from 'react-router';

import type { IconName } from '../../../assets/Icon/Icon';
import type {
  PreviewCardContentProps,
  PreviewCardHandle,
} from '../../../formatting/PreviewCard/PreviewCard';
import type { PortSpeed, PortStatus } from '../../Port/utils';
import { Divider } from '../../../assets/Divider/Divider';
import { Icon } from '../../../assets/Icon/Icon';
import { createShadowAnimationStack } from '../../../common/shadow_stacks';
import { connectionDrops, shadowParts } from '../../../common/shadows';
import { Tooltip } from '../../../components/Tooltip/Tooltip';
import { Button } from '../../../controls/Button/Button';
import { selectors, transitions } from '../../../controls/shared/styles';
import {
  PreviewCard,
  PreviewCardContent,
  PreviewCardTrigger,
} from '../../../formatting/PreviewCard/PreviewCard';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { Text } from '../../../text/Text';
import { AlignStack } from '../../../utilities/AlignStack/AlignStack';
import { VStack } from '../../../utilities/Stack/VStack';
import {
  useChassisTraceAnchor,
  useChassisTraceHighlighted,
} from '../../Chassis/atoms/ChassisTraces';
import { chassisVars } from '../../Chassis/atoms/utils';
import { getPortVariant } from '../../Port/utils';
import { slotVars } from './utils';

const slotChipSizing = {
  height: 20,
  icon: 12,
  padding: 4,
  gap: 4,
  /**
   * Two ports' width, which the two-tier stagger is what pays for. The binding gap is the 3px to the
   * tail of the chip one 40px pitch over, not the 8px to the lane-mate 80px away.
   */
  maxWidth: 72,
  radius: chassisVars.slot.radius,
  tierGap: 4,
  /** The stem between chip and slot. The far tier's clears a whole near chip plus `tierGap`. */
  tail: { near: 8, far: 32 },
  tailWidth: 2,
  /**
   * Where the tail stops being the chip's colour and where it has finished becoming the slot's.
   * Proportions, so the near tier's 8px and the far tier's 32px fade alike.
   */
  tailStops: { hold: '40%', settle: '75%' },
  /**
   * The selected fill, held clear of the pill's edge so it never meets the speed-coloured ring. Its
   * radius is the pill's less the inset, which keeps the two concentric.
   */
  selectedFill: { inset: 2, radius: 4 },
  /** `height` + `tail`, shared with `ChassisTraces` so its lines clear the chips. */
  offset: slotVars.chipLane,
};

const SlotChipIcon = styled(Icon, {
  flexShrink: 0,
  size: `$${slotChipSizing.icon}`,
  color: '$$slotChipIconColor',
});
SlotChipIcon.displayName = 'SlotChipIcon';

const SlotChipLabel = styled('span', {
  minWidth: 0,
  overflow: 'hidden',
  color: colors.bodyNeutralLight,
  fontFamily: '$sans',
  fontSize: '$10',
  fontWeight: '$bold',
  lineHeight: `$${slotChipSizing.icon}`,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  variants: {
    selected: {
      true: {
        color: colors.bodyBrandLight,

        [darkThemeSelector]: {
          color: colors.bodyBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotChipLabel.displayName = 'SlotChipLabel';

/**
 * The chip's ring is restated rather than taken from `shadows.connectionLight` so that speed can
 * colour it — which also puts it outside the stack `shadows` pads, so it's padded against the halo
 * here. Both ends render as they would unpadded; the padding is only what lets focus fade in.
 */
const slotChipShadows = {
  light: createShadowAnimationStack({
    resting: ['0 0 0 1px $$slotChipStrokeColor', ...connectionDrops.light],
    focused: shadowParts.focusRingLight,
  }),
  dark: createShadowAnimationStack({
    resting: ['0 0 0 1px $$slotChipStrokeColor', ...connectionDrops.dark],
    focused: shadowParts.focusRingDark,
  }),
};

const SlotChipPosition = styled('div', {
  position: 'relative',
  // A stacking context, so the fill below can sit under the icon and label rather than over them.
  isolation: 'isolate',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // The one hit target in the chip. See `SlotChipContainer`, which gives up its own to get here.
  pointerEvents: 'auto',
  gap: `$${slotChipSizing.gap}`,
  height: `$${slotChipSizing.height}`,
  // A chip with nothing to say is square: the padding either side of a 12px icon comes to 20.
  minWidth: `$${slotChipSizing.height}`,
  maxWidth: `$${slotChipSizing.maxWidth}`,
  padding: `0 $${slotChipSizing.padding}`,
  borderRadius: `$${slotChipSizing.radius}`,
  backgroundColor: colors.bgApplicationLight,
  $$slotChipSelectedColor: 'transparent',
  boxShadow: slotChipShadows.light.resting,
  transition: transitions.focusRing,

  // Selection. See `slotChipSizing.selectedFill` for why it stops short of the edge.
  '&::before': {
    content: '""',
    position: 'absolute',
    zIndex: -1,
    inset: `$${slotChipSizing.selectedFill.inset}`,
    borderRadius: `$${slotChipSizing.selectedFill.radius}`,
    backgroundColor: '$$slotChipSelectedColor',
  },

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: slotChipShadows.dark.resting,
  },

  // Replaces the ring rather than stacking with it — a focused chip says that before anything else.
  [selectors.focus]: {
    outline: 'none',
    boxShadow: slotChipShadows.light.focused,

    [darkThemeSelector]: {
      boxShadow: slotChipShadows.dark.focused,
    },
  },

  variants: {
    // Fills `&::before` above, so the ring and the tail are left carrying the port's speed.
    selected: {
      true: {
        $$slotChipSelectedColor: colors.bgBrandLight,

        [darkThemeSelector]: {
          $$slotChipSelectedColor: colors.bgBrandDark,
        },
      },
      false: {},
    },
    /*
     * Tints the icon and leaves the fill alone. A pill of saturated colour would out-shout the glyph
     * it belongs to, and the pale fill is what makes the chip read as floating above the card.
     *
     * Selection resolves to `brand` before it gets here rather than writing this property from the
     * `selected` group too, where the winner would come down to stitches' injection order.
     */
    variant: {
      alternative: {
        $$slotChipIconColor: colors.iconAlternativeLight,

        [darkThemeSelector]: { $$slotChipIconColor: colors.iconAlternativeDark },
      },
      attention: {
        $$slotChipIconColor: colors.iconAttentionLight,

        [darkThemeSelector]: { $$slotChipIconColor: colors.iconAttentionDark },
      },
      brand: {
        $$slotChipIconColor: colors.iconBrandLight,

        [darkThemeSelector]: { $$slotChipIconColor: colors.iconBrandDark },
      },
      negative: {
        $$slotChipIconColor: colors.iconNegativeLight,

        [darkThemeSelector]: { $$slotChipIconColor: colors.iconNegativeDark },
      },
      neutral: {
        $$slotChipIconColor: colors.iconNeutralLight,

        [darkThemeSelector]: { $$slotChipIconColor: colors.iconNeutralDark },
      },
      positive: {
        $$slotChipIconColor: colors.iconPositiveLight,

        [darkThemeSelector]: { $$slotChipIconColor: colors.iconPositiveDark },
      },
    },
  },
});
SlotChipPosition.displayName = 'SlotChipPosition';

/** Fades from the chip's colour to `SlotTarget`'s own outline, so the line lands as the stroke it joins. */
const tailFade = (landingColor: (typeof colors)[keyof typeof colors]) =>
  `linear-gradient($$slotChipTailDirection, $$slotChipStrokeColor ${slotChipSizing.tailStops.hold}, ${landingColor} ${slotChipSizing.tailStops.settle})`;

/** The stem between chip and slot. See `slotChipSizing.tail`. */
const SlotChipTail = styled('div', {
  width: `$${slotChipSizing.tailWidth}`,
  borderRadius: `$${slotChipSizing.tailWidth}`,
  // Fallback for a missing gradient direction, not a second layer.
  backgroundColor: '$$slotChipStrokeColor',
  backgroundImage: tailFade(colors.strokeNeutralLight),

  [darkThemeSelector]: {
    backgroundImage: tailFade(colors.strokeNeutralDark),
  },

  variants: {
    tier: {
      near: { height: `$${slotChipSizing.tail.near}` },
      far: { height: `$${slotChipSizing.tail.far}` },
    },
  },
});
SlotChipTail.displayName = 'SlotChipTail';

export const SlotChipContainer = styled('div', {
  position: 'absolute',
  zIndex: slotVars.zIndex.connection,
  // Centred on the slot rather than on a pinned width, so a slot widened by its number chip ('LAN0')
  // still carries its chip down the middle.
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  transform: 'translateX(-50%)',

  // Read by both the pill's ring and the tail, so they always agree.
  $$slotChipStrokeColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    $$slotChipStrokeColor: colors.strokeNeutralDark,
  },

  /*
   * Most of this box is the air either side of a 2px tail, and in the far tier that air lies across
   * the near chip beside it — so whichever painted last used to swallow the other's hover. The pill
   * takes the hit target back; `:hover` still reaches here from it, so the lift below is unaffected.
   */
  pointerEvents: 'none',

  /*
   * Brings a covered name out from under its neighbours. Not on selection too: a raised chip buries
   * the icon of the one to its left, and selection lasts as long as the drawer does.
   */
  '&:hover, &:focus-within': {
    zIndex: slotVars.zIndex.connectionRaised,
  },

  variants: {
    /** Clears the line being followed, which is drawn over the ports and so over the chips too. */
    raised: {
      true: {
        zIndex: slotVars.zIndex.connectionRaised,
      },
      false: {},
    },
    /**
     * Which edge of the slot the chip hangs off. `Slot` points it away from the number chip, so a chip
     * always sits on the far side of the glyph — over the group bracket above a top row and past the
     * card's edge below a bottom one. Both crossings are deliberate: a chip belongs to its port, not
     * to the caption or the card.
     *
     * The tail is always the second child and the direction flips instead, so it stays between the
     * chip and the slot on either edge.
     */
    orientation: {
      // The tail is the second child either way, so the direction it fades in flips with the stack.
      top: { flexDirection: 'column', $$slotChipTailDirection: 'to bottom' },
      bottom: { flexDirection: 'column-reverse', $$slotChipTailDirection: 'to top' },
    },
    /** Which of the edge's two lanes the chip hangs in. See `slotChipSizing.maxWidth`. */
    tier: { near: {}, far: {} },
    /**
     * What the ring and the tail draw in: the colour `Port` gives the port, from `getPortVariant`.
     * There is a case for each variant that resolves to, `neutral` leaving the chrome ring alone.
     */
    stroke: {
      alternative: {
        $$slotChipStrokeColor: colors.hardwareStrokeAlternativeLight,

        [darkThemeSelector]: { $$slotChipStrokeColor: colors.hardwareStrokeAlternativeDark },
      },
      attention: {
        $$slotChipStrokeColor: colors.hardwareStrokeAttentionLight,

        [darkThemeSelector]: { $$slotChipStrokeColor: colors.hardwareStrokeAttentionDark },
      },
      negative: {
        $$slotChipStrokeColor: colors.hardwareStrokeNegativeLight,

        [darkThemeSelector]: { $$slotChipStrokeColor: colors.hardwareStrokeNegativeDark },
      },
      neutral: {},
      positive: {
        $$slotChipStrokeColor: colors.hardwareStrokePositiveLight,

        [darkThemeSelector]: { $$slotChipStrokeColor: colors.hardwareStrokePositiveDark },
      },
    },
  },

  // The offset is the container's own height, so it can't come from either variant alone.
  compoundVariants: [
    { orientation: 'top', tier: 'near', css: { top: `-$${slotChipSizing.offset.near}` } },
    { orientation: 'top', tier: 'far', css: { top: `-$${slotChipSizing.offset.far}` } },
    { orientation: 'bottom', tier: 'near', css: { bottom: `-$${slotChipSizing.offset.near}` } },
    { orientation: 'bottom', tier: 'far', css: { bottom: `-$${slotChipSizing.offset.far}` } },
  ],
});
SlotChipContainer.displayName = 'SlotChipContainer';

export type SlotChipVariant =
  | 'alternative'
  | 'attention'
  | 'brand'
  | 'negative'
  | 'neutral'
  | 'positive';

/** Which of an edge's two lanes a chip hangs in. See `SlotChipContainer`'s `tier`. */
export type SlotChipTier = 'far' | 'near';

/**
 * The card that opens when a chip is pointed at, for connections with more to say than their name.
 *
 * `content` is the body under the header; everything else tunes the popup itself and passes straight
 * to `PreviewCardContent`. `side` defaults to the edge the chip hangs off, so a card opens away from
 * the faceplate rather than over it.
 */
export type SlotChipPreview = {
  content: ReactNode;
} & Omit<PreviewCardContentProps, 'children'>;

/** What a chip hands to the preview card — its own identity plus whatever the caller supplied. */
export type SlotChipPreviewPayload = SlotChipPreview & {
  icon: IconName;
  label: ReactNode;
  onClick?: (event: any) => void;
  variant?: SlotChipVariant;
  as?: ElementType;
  to?: To;
};

/**
 * A chip's preview card: the chip's own icon and name as a header, then whatever the caller passed as
 * `content`.
 *
 * Exported because a faceplate can host one card for all of its chips — see `SlotChipProps.previewHandle`.
 */
export function SlotChipPreviewContent({ payload }: { payload: SlotChipPreviewPayload }) {
  const { content, icon, label, onClick, variant, as, to, viewport, ...previewCardProps } = payload;
  const canFollow = Boolean((as && to) || onClick);

  return (
    <PreviewCardContent viewport={viewport ?? true} {...previewCardProps}>
      {/*
        Single child under the viewport, so Base UI can tag it `data-current` / `data-previous` and
        cross-fade when one shared card moves between chips.
      */}
      <VStack spacing={12}>
        <AlignStack
          gap={6}
          preset="body"
          start={<Icon icon={icon} size={14} variant={variant} />}
          end={
            canFollow ? (
              <Button
                as={as}
                to={to}
                onClick={onClick}
                arrangement="hidden-label"
                ghost
                icon="arrow-right"
                size="small"
                variant="secondary"
              >
                View
              </Button>
            ) : undefined
          }
        >
          <Text context="heading" variant="neutral" weight="bold">
            {label}
          </Text>
        </AlignStack>
        <Divider direction="row" />
        {content}
      </VStack>
    </PreviewCardContent>
  );
}

/**
 * The one card a faceplate's chips share, for `SlotChipProps.previewHandle`.
 *
 * Render it once anywhere inside the same tree as the chips — it draws nothing until a chip is pointed
 * at, and it reads which chip from the handle rather than from where it sits.
 */
export function SlotChipPreviewCard({
  handle,
}: {
  handle: PreviewCardHandle<SlotChipPreviewPayload>;
}) {
  return (
    <PreviewCard handle={handle}>
      {(props: { payload: unknown }) =>
        props.payload != null ? (
          <SlotChipPreviewContent payload={props.payload as SlotChipPreviewPayload} />
        ) : null
      }
    </PreviewCard>
  );
}

export type SlotChipProps = {
  /** Glyph for what is on the other end — a client, a device, an ISP. */
  icon: IconName;
  /** Name of the thing connected. Drawn beside the icon, ellipsed to fit, in full on hover. */
  label: ReactNode;
  /**
   * Whether the slot this chip belongs to is muted. The chip itself fades with the slot — it sits
   * inside the slot's own opacity — but a line anchored here has to be told, so it can fade too.
   */
  muted?: boolean;
  onClick?: (event: any) => void;
  /** Which way the chip hangs off the slot. `Slot` derives this from its own orientation. */
  orientation?: 'bottom' | 'top';
  /**
   * Detail to show on hover instead of the plain tooltip — an address, a speed, a serial.
   *
   * A chip is one icon, so the label alone leaves the reader guessing at everything except the name.
   * With this set the tooltip is replaced outright rather than stacked with: two hover popups on one
   * trigger fight each other.
   */
  preview?: SlotChipPreview;
  /**
   * Shares one preview card across every chip that passes the same handle, from
   * `createPreviewCardHandle()`.
   *
   * Worth it on a faceplate: with a card each, moving along a row of 48 ports dismisses and remounts
   * a popup per chip, where one shared card slides to the chip under the pointer and cross-fades its
   * contents. Pair it with one `SlotChipPreviewCard` holding the same handle. Without it each chip
   * mounts its own card, which is fine for a handful.
   */
  previewHandle?: PreviewCardHandle<SlotChipPreviewPayload>;
  /** Fills the pill short of its edge, so `speed` keeps the ring and the tail. */
  selected?: boolean;
  /**
   * Colours the tail and the pill's ring to match the glyph, chosen or not. Negotiated, not
   * rated — a 10G port sitting at 1G is the case worth seeing. Off, both stay neutral.
   */
  speed?: PortSpeed;
  /**
   * The port's state, which outranks `speed` the same way it does on the glyph: a port in error goes
   * red rather than holding the speed it last came up at. Defaults to `connected`, so a chip given a
   * speed alone still gets the speed's colour.
   */
  status?: PortStatus;
  /** Which of the edge's two lanes to hang in. `ChassisPorts` derives this from the layout. */
  tier?: SlotChipTier;
  /**
   * Names this chip as an endpoint its chassis can draw lines to, via `Chassis`'s `traces`.
   *
   * The chip rather than the slot, because the chip is where a line should land: it hangs in the
   * free space off the faceplate's outer edge, which is the only room a line has to travel in.
   * Unique across the whole card — a dual switch numbers both halves from 1.
   */
  traceId?: string;
  /** Tints the icon, for connections that mean something more specific than "something's here". */
  variant?: SlotChipVariant;
  // Routing — renders the chip as a link to whatever is connected.
  as?: ElementType;
  to?: To;
};

/**
 * What is plugged into a slot: an icon and a name on a pill, on a tail off the slot's outer edge.
 *
 * The pill takes the width of its name up to a cap, and neighbouring chips stagger between two tiers
 * so that cap is two ports wide rather than one — a faceplate of identical icons says only that
 * something is connected. Hover or focus lifts a chip clear, and the tooltip carries the name in full
 * for the ones long enough to ellipse.
 *
 * The pressable element is the pill itself, not the box positioning it, so the focus ring traces what
 * you can actually see.
 */
export function SlotChip({
  icon,
  label,
  muted = false,
  onClick,
  orientation = 'top',
  preview,
  previewHandle,
  selected = false,
  speed,
  status,
  tier = 'near',
  traceId,
  variant = 'neutral',
  as,
  to,
}: SlotChipProps) {
  const isPressable = !!(as || to || onClick);
  const traceRef = useChassisTraceAnchor(traceId, orientation, muted);
  const isTraced = useChassisTraceHighlighted(traceId);

  const slotChipContainerProps = {
    orientation,
    raised: isTraced,
    // Resolved the way the glyph's colour is, so the two can't disagree. Told neither, the ring
    // stays chrome — a chip with nothing to say about its port.
    stroke:
      speed === undefined && status === undefined
        ? undefined
        : getPortVariant(speed, status ?? 'connected'),
    tier,
  };

  /*
   * A chip earns a tab stop by having somewhere to go *or* something to say.
   *
   * The card is the only place its contents appear — an uplink's address, a device's serial — and
   * Base UI opens it on focus as readily as on hover, so a chip carrying one has to be reachable
   * whether or not it also navigates. An ISP is the case that made this show: it is a real
   * connection with a card, but there is no drawer to open for it, and tying focus to `onClick`
   * alone left its address and controller IP available to the mouse only.
   *
   * `role` still follows pressability rather than focus. A chip that goes nowhere is not a button,
   * and saying so would promise a press that does nothing.
   */
  const slotChipPositionProps = {
    as,
    to,
    onClick,
    tabIndex: isPressable || preview ? 0 : undefined,
    role: isPressable ? 'button' : undefined,
    selected,
    variant: selected ? ('brand' as const) : variant,
  };

  const slotChipContents = (
    <>
      <SlotChipIcon icon={icon} />
      {label ? <SlotChipLabel selected={selected}>{label}</SlotChipLabel> : null}
    </>
  );

  if (preview) {
    const payload: SlotChipPreviewPayload = {
      // Away from the faceplate, so the card never covers its own port. Sideways for a top chip: a
      // faceplate sits near the top of the page, so a card asking for the room above flips down.
      side: orientation === 'top' ? 'right' : 'bottom',
      ...preview,
      icon,
      label,
      onClick,
      variant,
      as,
      to,
    };

    const slotChipTrigger = (
      <PreviewCardTrigger
        // Short: travel from chip to card is already protected by the trigger's safe polygon, so
        // the delay only sets how quickly the card hands over between chips.
        closeDelay={100}
        delay={50}
        handle={previewHandle}
        payload={previewHandle ? payload : undefined}
        render={({ ref, ...slotChipTriggerProps }) => (
          <SlotChipPosition ref={ref} {...slotChipTriggerProps} {...slotChipPositionProps}>
            {slotChipContents}
          </SlotChipPosition>
        )}
      />
    );

    return (
      <SlotChipContainer ref={traceRef} {...slotChipContainerProps}>
        {previewHandle ? (
          slotChipTrigger
        ) : (
          <PreviewCard>
            {slotChipTrigger}
            <SlotChipPreviewContent payload={payload} />
          </PreviewCard>
        )}
        <SlotChipTail tier={tier} />
      </SlotChipContainer>
    );
  }

  return (
    <SlotChipContainer ref={traceRef} {...slotChipContainerProps}>
      <Tooltip contents={label}>
        <SlotChipPosition {...slotChipPositionProps}>{slotChipContents}</SlotChipPosition>
      </Tooltip>
      <SlotChipTail tier={tier} />
    </SlotChipContainer>
  );
}
